import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Plane } from "@react-three/drei";
import { forwardRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface PlaneMeshProperties extends TransformableItemProperties {
  width: number;
  height: number;
  widthSegments: number;
  heightSegments: number;
  materialId?: string;
}

export interface PlaneMesh extends Mesh<PlaneMeshProperties> {
  subtype: 'plane';
  properties: PlaneMeshProperties;
}

// Create a reusable Plane component that can be used in both preview and world
const PlaneComponent = forwardRef<THREE.Mesh, { properties: PlaneMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { width, height, widthSegments, heightSegments, transform, materialId } = properties;
  const { activeWorld } = useAppContext();

  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      const mat = material.render(material.properties, isPreview);
      mat.side = THREE.DoubleSide;
      return mat;
    }
    return new THREE.MeshStandardMaterial({ color: "#808080", side: THREE.DoubleSide });
  }, [material, material?.properties, isPreview]);

  return (
    <Plane
      ref={ref}
      args={[width, height, widthSegments, heightSegments]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const PlaneMeshItem: PlaneMesh = {
  id: '',
  name: 'Plane',
  type: 'mesh',
  subtype: 'plane',
  icon: 'Plane',
  properties: {
    width: 1,
    height: 1,
    widthSegments: 1,
    heightSegments: 1,
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.width',
      type: 'number',
      label: 'Width',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.height',
      type: 'number',
      label: 'Height',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.widthSegments',
      type: 'number',
      label: 'Width Segments',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.heightSegments',
      type: 'number',
      label: 'Height Segments',
      default: 1,
      panel: 'geometry'
    }
  ],
  render: (properties: PlaneMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <PlaneComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 